import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { format, fromUnixTime } from 'date-fns';
import { maxBy, meanBy, minBy, sumBy, groupBy } from 'lodash';
import { orderBy } from 'lodash';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useSubscription } from '@apollo/client';
import { IconPlug } from '../../../../../../assets/control-icons/icon-plug';
import { IconError, IconSchedule } from '../../../../../../assets/icons';
import { ControlWrapper, NavHeader, Page } from '../../../../../../components';
import { PHASES_COLORS } from '../../../../../../const';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  MeasurementChannelKind,
  ScalarParameterTypeInternal,
  VectorParameterTypeInternal,
  OptimizerScalarParameterTypeInternal,
  OptimizerVectorParameterTypeInternal,
  ChannelTypeInternal,
  MeterTypeInternal,
  MeasurementSubscriptionError,
  OnLiveMeasurementSubscriptionVariables,
  MeasurementSubscriptionResponse,
  OnLiveMeasurementSubscription,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_LIVE_MEASUREMENT } from '../../../../../../data-access/subscriptions/on-measurement';
import { SelectedPhase } from '../../../../../../types';
import { convertDateToFormat } from '../../../../../../utils/helpers';
import { getParameterByName } from '../../../../../../utils/location';
import { useDevicesAndChannels } from '../../../../hooks/devices-and-channels';
import { useMeasurementMapOperations } from '../../../../hooks/measurement-map/use-measurement-map-operations';
import { ChannelMeterType, ChannelType } from '../../../../types';
import { ChannelOptimizerType } from '../../../../types/channel/optimizer';
import '../../meter/analysis/components/summary/index.scss';
import { measurementNameParse } from '../../meter/utils';
import { PhasesButtonGroup } from '../components/phases-button-group';
import { averageSummaryMeasurements, measurementParameters, measurementParametersOptimizer } from '../utils';
import { getMeasurementValue, getPhaseMeasurements } from '../utils/measurement-details';
import { CurrentValue } from './current-value';
import { DownloadMeasurementsData } from './download-data';
import './index.scss';
import { LiveTable } from './table';
import { LiveTooltip } from './tooltip';

const strokeLineColor = '#9A9BA2';

export type MeasurementDetailItem = {
  value: number;
  timestamp: number;
  date: string;
  phasesValues?: number[];
};

export enum MeasurementGroupType {
  SinglePhase = 'SinglePhase',
  Absolute = 'Absolute',
}

const MeasurementDetails: React.FC = () => {
  const { t } = useTranslation('channel-details');
  const { measurementMap, convertMeasurementToNumber } = useMeasurementMapOperations();
  const { skipLavvaFetch } = useInstallationContext();
  const { channelId, measurementGroup, measurement, kind } = useParams<{
    channelId: string;
    kind: MeasurementChannelKind;
    measurementGroup: MeasurementGroupType;
    measurement:
      | VectorParameterTypeInternal
      | ScalarParameterTypeInternal
      | OptimizerVectorParameterTypeInternal
      | OptimizerScalarParameterTypeInternal;
  }>();
  const Measurement = measurement ?? VectorParameterTypeInternal.Unknown;
  const MeasurementGroup = measurementGroup ?? MeasurementGroupType.Absolute;
  const phase = getParameterByName('phase');
  const [selectedPhases, setSelectedPhases] = useState<number[]>(phase ? [parseInt(phase)] : []);
  const [phaseTabs, setPhaseTabs] = useState<number[]>([]);
  const { channelList } = useDevicesAndChannels({ channelId });
  const [chartData, setChartData] = useState<MeasurementDetailItem[]>([]);
  const [currentValue, setCurrentValue] = useState<number | null>(null);

  const channel = useMemo(() => {
    return channelList.find((channel: ChannelType) => channel && channel?.id === channelId) as ChannelType;
  }, [channelId, channelList]);

  const supportedScalars = (channel?.data as ChannelMeterType | ChannelOptimizerType | undefined)
    ?.supportedScalarParameters;
  const supportedVectors = (channel?.data as ChannelMeterType | ChannelOptimizerType | undefined)
    ?.supportedVectorParameters;

  const parameters =
    kind === MeasurementChannelKind.Meter
      ? measurementParameters[Measurement]
      : measurementParametersOptimizer[Measurement];

  const { data } = useSubscription<OnLiveMeasurementSubscription, OnLiveMeasurementSubscriptionVariables>(
    ON_LIVE_MEASUREMENT,
    {
      variables: {
        input: {
          channelId,
          deviceId: channel?.deviceId,
          kind: kind as MeasurementChannelKind,
          scalarParameterTypes: supportedScalars || [],
          vectorParameterTypes: supportedVectors || [],
          indices: [],
        },
      },
      skip: !measurementMap || skipLavvaFetch || !kind || !channel,
    },
  );

  const meterMeasurement = useMemo(() => {
    const isAbsolute = MeasurementGroup === MeasurementGroupType.Absolute;
    const measurementNumber = convertMeasurementToNumber(kind)(isAbsolute ? 'scalars' : 'vectors', Measurement);

    return {
      ...data,
      onLiveMeasurement: {
        ...data?.onLiveMeasurement,
        data: {
          ...data?.onLiveMeasurement?.data,
          vectorMeasurements: isAbsolute
            ? []
            : (data?.onLiveMeasurement?.data as MeasurementSubscriptionResponse)?.vectorMeasurements?.filter(
                (scalar) => scalar.type === measurementNumber,
              ),
          scalarMeasurements: isAbsolute
            ? (data?.onLiveMeasurement?.data as MeasurementSubscriptionResponse)?.scalarMeasurements?.filter(
                (scalar) => scalar.type === measurementNumber,
              )
            : [],
        },
      },
    } as OnLiveMeasurementSubscription;
  }, [data]);

  const convertValue = (value: number) => {
    if (kind === MeasurementChannelKind.Optimizer && Measurement === OptimizerVectorParameterTypeInternal.ActivePower) {
      return value / 1000;
    }

    return value;
  };

  useEffect(() => {
    if (meterMeasurement?.onLiveMeasurement?.data) {
      const data = meterMeasurement.onLiveMeasurement.data;

      if ('unixTimestamp' in data && data.unixTimestamp) {
        const phaseMeasurements = getPhaseMeasurements(MeasurementGroup, meterMeasurement);
        const phasesValues: number[] = phaseMeasurements?.map((phaseMeasurement) => phaseMeasurement.value);
        const summaryPhases = [{ value: 0 }] as SelectedPhase[];

        const summaryOfPhasesValue = getMeasurementValue(
          Measurement,
          selectedPhases,
          phasesValues,
          summaryPhases,
          MeasurementGroup,
        );
        const phaseTabs = Object.keys(groupBy(phaseMeasurements, 'index')).map((phase) => Number(phase));
        setPhaseTabs(phaseTabs);

        if (summaryOfPhasesValue !== undefined) {
          setCurrentValue(+summaryOfPhasesValue.toFixed(parameters.fixed));

          setChartData((prev) => {
            return [
              ...prev,
              {
                value: +convertValue(summaryOfPhasesValue).toFixed(parameters.fixed),
                timestamp: data.unixTimestamp,
                date: format(fromUnixTime(data.unixTimestamp), 'HH:mm:ss'),
                ...(MeasurementGroup === MeasurementGroupType.SinglePhase
                  ? {
                      phasesValues: phaseMeasurements.map(
                        (phaseMeasurement) => +convertValue(phaseMeasurement.value).toFixed(parameters.fixed),
                      ),
                    }
                  : {}),
              },
            ];
          });
        }
      }
    }
  }, [meterMeasurement?.onLiveMeasurement]);

  const isSummary = useMemo(() => {
    return !selectedPhases.length || (selectedPhases.length === 1 && selectedPhases[0] === 0);
  }, [selectedPhases]);

  const chartDataBySelectedPhases: MeasurementDetailItem[] = useMemo(
    () =>
      chartData.map((chartDataItem) => {
        if (chartDataItem.phasesValues) {
          const values = chartDataItem.phasesValues
            ?.map((phaseValue, index) => ({
              phase: index + 1,
              value: phaseValue,
            }))
            .filter((measurement) => {
              return !!selectedPhases.find((phase) => phase === measurement.phase);
            });

          return {
            ...chartDataItem,
            value: averageSummaryMeasurements.includes(Measurement as VectorParameterTypeInternal)
              ? +meanBy(values, 'value').toFixed(parameters.fixed)
              : +sumBy(values, 'value').toFixed(parameters.fixed),
          };
        }

        return {
          ...chartDataItem,
        };
      }),
    [chartData, selectedPhases],
  );

  const iconValue = useMemo(() => {
    if (Measurement === OptimizerVectorParameterTypeInternal.RelayState) return 'R';
    return parameters.shortcut ?? parameters.symbol ? parameters.symbol : parameters.unit;
  }, [parameters, Measurement]);

  const iconFontSize = useMemo(() => {
    switch (iconValue.length) {
      case 1:
      case 2:
        return 20;
      case 3:
        return 16;
      case 4:
        return 14;
      default:
        return 12;
    }
  }, [iconValue]);

  const itemPrefix = useMemo(() => {
    if (channel?.data?.type === ChannelTypeInternal.Optimizer) return t('phase');
    return !(
      channel?.data?.type === ChannelTypeInternal.Meter && channel.data.meterType === MeterTypeInternal.Standalone
    )
      ? t('outputShort')
      : t('phase');
  }, [channel]);

  const averageValueByPhases = useMemo(() => {
    let mean = meanBy(!isSummary ? chartDataBySelectedPhases : chartData, 'value');
    if (
      mean &&
      kind === MeasurementChannelKind.Optimizer &&
      Measurement === OptimizerVectorParameterTypeInternal.ActivePower
    ) {
      mean = mean / 1000;
    }

    return mean.toFixed(parameters.fixed);
  }, [selectedPhases, chartData, kind, Measurement]);

  const maxValueByPhases = useMemo(() => {
    let max = maxBy(!isSummary ? chartDataBySelectedPhases : chartData, 'value')?.value;
    if (
      max &&
      kind === MeasurementChannelKind.Optimizer &&
      Measurement === OptimizerVectorParameterTypeInternal.ActivePower
    ) {
      max = max / 1000;
    }

    if (max !== undefined) return max.toFixed(parameters.fixed);
  }, [selectedPhases, chartData, kind, Measurement]);

  const minValueByPhases = useMemo(() => {
    let min = minBy(!isSummary ? chartDataBySelectedPhases : chartData, 'value')?.value;
    if (
      min &&
      kind === MeasurementChannelKind.Optimizer &&
      Measurement === OptimizerVectorParameterTypeInternal.ActivePower
    ) {
      min = min / 1000;
    }

    if (min !== undefined) return min.toFixed(parameters.fixed);
  }, [selectedPhases, chartData, kind, Measurement]);

  const error = useMemo(
    () => (meterMeasurement?.onLiveMeasurement?.data as MeasurementSubscriptionError)?.errorCode || '',
    [meterMeasurement?.onLiveMeasurement],
  );

  const currentStateValue = useMemo(() => {
    if (currentValue === null) return '-';
    if (Measurement === OptimizerVectorParameterTypeInternal.RelayState) return <IconPlug isOn={!!currentValue} />;
    if (kind === MeasurementChannelKind.Optimizer && Measurement === OptimizerVectorParameterTypeInternal.ActivePower) {
      return (currentValue / 1000).toFixed(parameters.fixed);
    } else {
      return currentValue.toFixed(parameters.fixed);
    }
  }, [kind, Measurement, currentValue, parameters]);

  const handleSelectPhases = (options: number[]) => {
    setSelectedPhases(options);

    if (meterMeasurement?.onLiveMeasurement?.data) {
      const data = meterMeasurement.onLiveMeasurement.data;

      if ('unixTimestamp' in data && data.unixTimestamp) {
        const summaryPhases = [{ value: 0 }] as SelectedPhase[];
        const phaseMeasurements = getPhaseMeasurements(MeasurementGroup, meterMeasurement);
        const phasesValues: number[] = phaseMeasurements.map((phaseMeasurement) => phaseMeasurement.value);

        const currentValue = getMeasurementValue(Measurement, options, phasesValues, summaryPhases, MeasurementGroup);

        if (currentValue) {
          setCurrentValue(+currentValue.toFixed(parameters.fixed));
        }
      }
    }
  };

  const showMultiPhasesAreas = MeasurementGroup === MeasurementGroupType.SinglePhase && !isSummary;
  const showLineChart =
    showMultiPhasesAreas && averageSummaryMeasurements.includes(Measurement as VectorParameterTypeInternal);

  return (
    <Page className="measurements-details" header={<NavHeader />}>
      <div className="details-header">
        <div className="details-header--left">
          <div className="details-header__icon details-header__icon--text" style={{ fontSize: iconFontSize }}>
            {iconValue}
          </div>
          <div className="details-header__name">
            <h2 className="text-ellipsis">
              {phase ? `${itemPrefix} ${phase}` : t('sumOfPhases')} -{' '}
              {t(
                `energyMeasurements.types.${
                  MeasurementGroup === MeasurementGroupType.SinglePhase ? 'phase' : 'summary'
                }.${measurementNameParse(Measurement)}`,
              )}
            </h2>
            <div className="details-header__type">{channel?.alias}</div>
          </div>
        </div>
      </div>
      <div className="details-page ">
        <ControlWrapper className="control-wrapper--column control-wrapper--reverse p-l-24 p-r-24 p-b-16 p-t-16 m-b-8">
          {error && <IconError />}
          <div className="date-picker">
            <div className="date-picker__date">
              <h1 className="date-picker__date-value">{convertDateToFormat(new Date(), 'eeee, dd.MM.yyyy')}</h1>
            </div>
          </div>
          <span className="tooltip__text tooltip__label m-b-16">{t('online')}</span>
          <div className="chart">
            <ResponsiveContainer width="100%" height={220}>
              {showLineChart ? (
                <LineChart data={chartData} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={strokeLineColor} strokeOpacity={0.2} />
                  <XAxis
                    dataKey="timestamp"
                    tickMargin={8}
                    axisLine={false}
                    interval={'preserveStartEnd'}
                    tickLine={{ stroke: 'transparent' }}
                    tickFormatter={(value) => format(fromUnixTime(value), 'HH:mm')}
                  />
                  <YAxis
                    axisLine={{ strokeWidth: '0' }}
                    tickCount={6}
                    tickMargin={6}
                    tickLine={{ stroke: strokeLineColor, strokeWidth: '0.5px', strokeOpacity: '0.2' }}
                    domain={['auto', 'auto']}
                    dataKey={'phasesValues'}
                  />
                  <Tooltip
                    content={(props) => (
                      <LiveTooltip
                        tooltipProps={props}
                        unit={parameters.unit}
                        fixed={parameters.fixed}
                        selectedPhases={selectedPhases}
                      />
                    )}
                    animationDuration={500}
                  />
                  {orderBy(selectedPhases, 'value', 'asc').map((selectedPhase) => {
                    return (
                      <Line
                        key={selectedPhase}
                        type="monotone"
                        dataKey={`phasesValues.${selectedPhase - 1}`}
                        stroke={PHASES_COLORS[selectedPhase]}
                        strokeWidth={2}
                        animationDuration={800}
                        animationEasing={'ease-in-out'}
                      />
                    );
                  })}
                </LineChart>
              ) : (
                <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={strokeLineColor} strokeOpacity={0.2} />
                  <XAxis
                    dataKey="timestamp"
                    tickMargin={8}
                    axisLine={false}
                    interval={'preserveStartEnd'}
                    tickLine={{ stroke: 'transparent' }}
                    tickFormatter={(value) => format(fromUnixTime(value), 'HH:mm')}
                  />
                  <YAxis
                    axisLine={{ strokeWidth: '0' }}
                    tickCount={6}
                    tickMargin={6}
                    tickLine={{ stroke: strokeLineColor, strokeWidth: '0.5px', strokeOpacity: '0.2' }}
                    {...(averageSummaryMeasurements.includes(Measurement as VectorParameterTypeInternal)
                      ? { domain: ['auto', 'auto'] }
                      : {})}
                  />
                  <Tooltip
                    content={(props) => (
                      <LiveTooltip
                        tooltipProps={props}
                        unit={parameters.unit}
                        fixed={parameters.fixed}
                        selectedPhases={selectedPhases}
                      />
                    )}
                    animationDuration={500}
                  />
                  {showMultiPhasesAreas ? (
                    <>
                      {orderBy(selectedPhases, 'value', 'asc').map((selectedPhase) => {
                        return (
                          <Area
                            key={selectedPhase}
                            dot={{
                              stroke: '#FFFFFF',
                              strokeWidth: 1.5,
                              fill: PHASES_COLORS[selectedPhase],
                              z: 2,
                            }}
                            type="monotone"
                            dataKey={`phasesValues.${selectedPhase - 1}`}
                            strokeWidth={2}
                            stroke={PHASES_COLORS[selectedPhase]}
                            fillOpacity={0.5}
                            fill={PHASES_COLORS[selectedPhase]}
                            animationDuration={800}
                            animationEasing={'ease-in-out'}
                            stackId={selectedPhase - 1}
                          />
                        );
                      })}
                    </>
                  ) : (
                    <Area
                      dot
                      type="monotone"
                      dataKey="value"
                      strokeWidth={2}
                      stroke={PHASES_COLORS[0]}
                      fillOpacity={1}
                      fill="#FFC6B9"
                      animationDuration={800}
                      animationEasing={'ease-in-out'}
                    />
                  )}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          <CurrentValue unit={parameters.unit} currentValue={currentStateValue} />
          {MeasurementGroup === MeasurementGroupType.SinglePhase && phaseTabs.length > 1 ? (
            <PhasesButtonGroup
              selectedPhases={selectedPhases}
              onSelectedPhases={handleSelectPhases}
              defaultPhases={!phase ? [0, ...(phaseTabs || [1, 2, 3])] : [...(phaseTabs || [1, 2, 3])]}
              itemPrefix={itemPrefix}
            />
          ) : null}
        </ControlWrapper>
        {Measurement !== OptimizerVectorParameterTypeInternal.RelayState && (
          <ControlWrapper className="control-wrapper--column  control-wrapper--height-auto control-wrapper--stretch p-l-0 p-r-0 p-b-16 p-t-16 m-b-8">
            <div className="summary max-width-desktop">
              <div className="summary__caption p-t-16 p-b-16">
                <IconSchedule />
                <span className="summary__caption-text">{t('energyMeasurements.summaryOnline')}</span>
              </div>
              <div className="summary__section">
                <div className="summary__section-row p-t-0 p-b-0">
                  <span>{t('summary.average')}</span>
                  <span className="summary__section-value">
                    {chartData.length ? averageValueByPhases : (0).toFixed(parameters.fixed)} {parameters.unit}
                  </span>
                </div>
              </div>
              <div className="summary__section">
                <div className="summary__section-row p-t-0 p-b-0">
                  <span>{t('summary.min')}</span>
                  <span className="summary__section-value">
                    {chartData.length ? minValueByPhases : (0).toFixed(parameters.fixed)} {parameters.unit}
                  </span>
                </div>
              </div>
              <div className="summary__section">
                <div className="summary__section-row p-t-0 p-b-0">
                  <span>{t('summary.max')}</span>
                  <span className="summary__section-value">
                    {chartData.length ? maxValueByPhases : (0).toFixed(parameters.fixed)} {parameters.unit}
                  </span>
                </div>
              </div>
            </div>
          </ControlWrapper>
        )}
        <ControlWrapper className="control-wrapper--column  p-l-24 p-r-24 p-b-24 p-t-24">
          <DownloadMeasurementsData
            unit={parameters.unit}
            data={chartData}
            measurementGroup={MeasurementGroup}
            selectedPhases={selectedPhases}
            isSummary={isSummary}
          />
          <LiveTable data={!isSummary ? chartDataBySelectedPhases : chartData} unit={parameters.unit} />
        </ControlWrapper>
      </div>
    </Page>
  );
};

export default MeasurementDetails;
